import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type Address } from '~/types/address';
import { type UUID } from '~/types/common';
import { type SiteTypeType } from '~/types/site';

import { vestigasApi } from '~/services/kyClient';

export type SitePayload = {
  name: string;
  type: SiteTypeType;
  coords?: {
    latitude: number | undefined;
    longitude: number | undefined;
  };
  address?: Address;
  start?: string; // ISO 8601 date string
  end?: string; // ISO 8601 date string
  companyId?: UUID;
  accountingReferenceList?: UUID | UUID[];
  orgUnits?: UUID | UUID[];
  orgUnitsSyncKeys?: string[];
  companySyncKey?: string;
  accRefSyncKeys?: string[];
  syncKey?: string | null;
};

type CreateSiteResponse = {
  code: number;
  id: UUID;
  message: string;
};

type CreateSiteMutationOptions = UseMutationOptions<
  CreateSiteResponse,
  Error,
  SitePayload
>;

export type CreateSiteMutateAsyncFunction = UseMutateAsyncFunction<
  CreateSiteResponse,
  Error,
  SitePayload
>;

/**
 * Creates a new site.
 *
 * @param {Object} siteData - The site data for creating a new site.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/create_site_site_post
 */
export const createSite = async (siteData: SitePayload) => {
  const response = await vestigasApi
    .post(ENDPOINT.SITE.CREATE(), { json: siteData })
    .json<CreateSiteResponse>();

  return response;
};

/**
 * Custom hook for creating a new site.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationCreateSite = (options?: CreateSiteMutationOptions) => {
  return useMutation({
    mutationFn: async (siteData: SitePayload) => createSite(siteData),
    ...options,
  });
};
